// @ts-strict-ignore
import type { ReactElement } from "react";

import { Skeleton, Spacer, Typography } from "@aviary";
import { ListGridSelector } from "@shared/components/ListGridSelector/ListGridSelector";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { MegaMenuFilterTags } from "../CatalogSearch/MegaMenuFilterTags/MegaMenuFilterTags";
import { CatalogSortDropdown } from "../CatalogSortDropdown/CatalogSortDropdown";

import { EmersonProductResultsHeader } from "./EmersonProductResultsHeader/EmersonProductResultsHeader";

import * as styles from "./ProductResultsHeader.styles";

interface Props {
  title: string | (ReactElement | string)[];
  resultCount?: number;
  isLoadingNewResults?: boolean;
  isDrawer?: boolean;
  isCareCenter?: boolean;
  onClearFilters?: () => void;
  isFS2Wholesale?: boolean;
}

const ProductResultsHeader = ({
  title,
  resultCount,
  isLoadingNewResults,
  isDrawer,
  isCareCenter = false,
  onClearFilters,
  isFS2Wholesale,
}: Props) => {
  const { phoneLarge, tablet } = useBreakpoints();

  const { isGridView, setGridView } = useCatalogSearch();
  const { isEmerson } = useSharedGlobalConfig();
  const isSortViewSelectorDisabled = !resultCount || resultCount <= 1;

  const renderTitle = () => {
    if (isLoadingNewResults) {
      return (
        <Skeleton type="typography" typographySize="h1" width={6} css={styles.titleSkeleton} />
      );
    }

    return (
      <Typography type="body" isMarginless css={styles.title} data-e2e="product-results-quantity">
        {title}
      </Typography>
    );
  };

  const renderSortDropdown = () => {
    if ((!isCareCenter && phoneLarge.greaterThan) || (isCareCenter && tablet.greaterThan)) {
      if (isLoadingNewResults) {
        return (
          <Skeleton
            type="typography"
            typographySize="h1"
            width={8}
            css={styles.sortDropdownSkeleton}
          />
        );
      }

      return <CatalogSortDropdown isDisabled={isSortViewSelectorDisabled} />;
    }

    return null;
  };

  const renderGridSelector = () => {
    if (isDrawer) return null;
    if (!isCareCenter && phoneLarge.greaterThan) {
      if (isLoadingNewResults) {
        return (
          <Skeleton type="typography" typographySize="h1" width={6} css={styles.gridListSelector} />
        );
      }

      return (
        <ListGridSelector
          isDisabled={isSortViewSelectorDisabled}
          isGridView={isGridView}
          handleToggle={setGridView}
          css={styles.gridListSelector}
        />
      );
    }

    return null;
  };

  if (isEmerson) {
    return (
      <EmersonProductResultsHeader
        title={title}
        resultCount={resultCount}
        isLoadingNewResults={isLoadingNewResults}
        onClearFilters={onClearFilters}
      />
    );
  }
  if (isCareCenter) {
    return (
      <div>
        {renderTitle()}
        <Spacer />
        <MegaMenuFilterTags
          onClearFilters={onClearFilters}
          resultCount={resultCount}
          isFS2Wholesale={isFS2Wholesale}
        />
      </div>
    );
  }

  return (
    <div css={styles.header}>
      {renderTitle()}
      <div css={styles.actions}>
        {renderSortDropdown()}
        {renderGridSelector()}
      </div>
    </div>
  );
};

export { ProductResultsHeader };
